import { EnumFeature, EnumScreenName } from '@calm/evexia-analytics';
import { routePattern, routes } from '@rhg/utils/routes';

// Important note: only the pathname is used as a key in this map.
// If you want to add distinct screen or feature names for different query parameters,
// you need to handle that explicitly in getAnalyticsForRoute
export const analyticsPathnameMap: Record<
    string,
    [EnumScreenName, EnumFeature] | undefined
> = {
    [routes.auth]: [EnumScreenName.Login, EnumFeature.LoginRegistration],
    [routes.onboarding({})]: [
        EnumScreenName.Onboarding,
        EnumFeature.FtueWorkflow,
    ],
    [routes.concierge({})]: [EnumScreenName.Onboarding, EnumFeature.Concierge],
    [routes.expired]: [EnumScreenName.Expired, EnumFeature.LossOfSponsorship],
    [routes.saml]: [EnumScreenName.Saml, EnumFeature.LoginRegistration],
    [routes.sso]: [EnumScreenName.SsoInFocus, EnumFeature.LoginRegistration],
    [routes.reconsent]: [EnumScreenName.Consent, EnumFeature.Consent],
    [routes.ssoInfo]: [EnumScreenName.SsoIn, EnumFeature.LoginRegistration],
    [routes.completed]: [
        EnumScreenName.HomeLibraryViewAllCompleted,
        EnumFeature.Home,
    ],
    [routes.dashboard]: [EnumScreenName.Landing, EnumFeature.Home],
    // The check-in flow tracks its own steps (see EmotionsCheckInFlowProvider)
    [routes.emotionsCheckIn()]: [
        EnumScreenName.EmotionSelector,
        EnumFeature.EmotionalCheckIn,
    ],
    // The results page fires its own Screen : Viewed with check-in props
    '/emotions-check-in/results': [
        EnumScreenName.EmotionalCheckinResults,
        EnumFeature.EmotionalCheckIn,
    ],
    // The history pages fire their own Screen : Viewed with check-in counts
    [routes.emotionsCheckInHistory]: [
        EnumScreenName.OverallEmotionalCheckinHistory,
        EnumFeature.EmotionalCheckIn,
    ],
    [routes.emotionsCheckInHistoryAll]: [
        EnumScreenName.EmotionalCheckinHistory,
        EnumFeature.EmotionalCheckIn,
    ],
    [routes.emotionsCheckInEmotionHistory('[emotion]')]: [
        EnumScreenName.SpecificEmotionalCheckinDetails,
        EnumFeature.EmotionalCheckIn,
    ],
    [routes.login]: [
        EnumScreenName.LoginRegistration,
        EnumFeature.LoginRegistration,
    ],

    [routes.discover]: [EnumScreenName.Discover, EnumFeature.Discover],
    [routes.discoverMindshift]: [
        EnumScreenName.MindshiftViewAll,
        EnumFeature.Mindshift,
    ],
    [routes.discoverPack('[packClass]')]: [
        EnumScreenName.DiscoverCategory,
        EnumFeature.Discover,
    ],
    // Not used for the route-level Screen : Viewed fire (feed-page fires its
    // own FeedDetails event and is skip-listed in analytics-provider) — this
    // entry exists so getScreenName(pathname) resolves correctly for anything
    // that reads the *current* route's screen name off this map directly
    // (e.g. the guide/program player's SourceScreenName), which would
    // otherwise fall back to NotMapped while the user is on a feed page.
    [routes.discoverFeed('[feedId]')]: [
        EnumScreenName.FeedDetails,
        EnumFeature.Discover,
    ],
    // The dedicated goal/topic detail pages currently render as stubs and are
    // not yet linked from the product (category/goal/topic view-all all resolve
    // to the shared /discover/pack/[packClass] route → DiscoverCategory). Map
    // them so they emit their own screen name instead of NotMapped once wired.
    // Distinguishing goal/topic on the shared pack route would need query-param
    // branching in getAnalyticsForRoute and is deferred.
    [routePattern.discoverGoal]: [
        EnumScreenName.DiscoverGoal,
        EnumFeature.Discover,
    ],
    [routePattern.discoverTopic]: [
        EnumScreenName.DiscoverTopic,
        EnumFeature.Discover,
    ],
    [routes.discoverDailies('')]: [
        EnumScreenName.DailyDetail,
        EnumFeature.Discover,
    ],
    [routes.discoverDailies('[dailyId]')]: [
        EnumScreenName.DailyDetail,
        EnumFeature.Discover,
    ],
    [routes.discoverClinical]: [
        EnumScreenName.DiscoverClinical,
        EnumFeature.Discover,
    ],
    [routes.search]: [EnumScreenName.Search, EnumFeature.Search],
    [routes.logout]: [EnumScreenName.LoggedOut, EnumFeature.LoginRegistration],
    [routes.health]: [EnumScreenName.Health, EnumFeature.Health],
    [routes.careplan]: [EnumScreenName.CarePlan, EnumFeature.CarePlan],
    [routes.carePlanAllRecommendations]: [
        EnumScreenName.CarePlanAllRecommendations,
        EnumFeature.CarePlan,
    ],
    [routes.profile]: [EnumScreenName.Profile, EnumFeature.Profile],
    [routes.recent]: [
        EnumScreenName.HomeLibraryViewAllRecentlyPlayed,
        EnumFeature.Home,
    ],
    [routes.settings]: [EnumScreenName.Settings, EnumFeature.Profile],
    [routes.notifications()]: [
        EnumScreenName.Notifications,
        EnumFeature.UkCanada,
    ],
    [routes.signup]: [EnumScreenName.Signup, EnumFeature.LoginRegistration],
    [routes.favorites]: [
        EnumScreenName.HomeLibraryViewAllFavorites,
        EnumFeature.Home,
    ],
    [routes.sponsor]: [EnumScreenName.Sponsor, EnumFeature.Profile],
    [routes.goals({ isFtue: false })]: [
        EnumScreenName.GoalSelection,
        EnumFeature.FtueSurvey,
    ],
    [routes.topics({ isFtue: false })]: [
        EnumScreenName.TopicSelection,
        EnumFeature.FtueSurvey,
    ],
    [routes.conditions({ isFtue: false })]: [
        EnumScreenName.Conditions,
        EnumFeature.Conditions,
    ],
    [routes.registration]: [
        EnumScreenName.Registration,
        EnumFeature.LoginRegistration,
    ],
    [routes.guideBase]: [EnumScreenName.GuidePlayer, EnumFeature.Guides],
    [routes.programBase]: [EnumScreenName.ProgramDetail, EnumFeature.Programs],
    [routes.playlist]: [EnumScreenName.Playlist, EnumFeature.Playlists],
    [routes.notFound]: [EnumScreenName.NotFound, EnumFeature.Error],
    [routes.error({})]: [EnumScreenName.Error, EnumFeature.Error],
    // These are covered by analyticsRouteQueryMap these are fallbacks
    [routes.screening({ isFtue: false })]: [
        EnumScreenName.MentalHealthSurveyStart,
        EnumFeature.MentalHealthSurvey,
    ],
    [routes.results({ isFtue: false })]: [
        EnumScreenName.MentalHealthSurveyResults,
        EnumFeature.MentalHealthSurvey,
    ],
    [routes.standaloneScreening]: [
        EnumScreenName.StandaloneScreening,
        EnumFeature.StandaloneScreening,
    ],
    [routes.standaloneResults]: [
        EnumScreenName.StandaloneScreeningResults,
        EnumFeature.StandaloneScreening,
    ],
    [routes.nonUsResident]: [
        EnumScreenName.NonUsResidentBlocker,
        EnumFeature.StandaloneScreening,
    ],
    [routes.profileInvite({})]: [
        EnumScreenName.DependentInviteManage,
        EnumFeature.FriendsAndFamily,
    ],
    [routes.journaling]: [EnumScreenName.Journaling, EnumFeature.Journaling],
    [routes.journalEntry({})]: [
        EnumScreenName.JournalEntry,
        EnumFeature.Journaling,
    ],
    [routes.journalEntryReview({})]: [
        EnumScreenName.JournalEntryReview,
        EnumFeature.Journaling,
    ],
    [routes.playlists]: [EnumScreenName.YourPlaylists, EnumFeature.Playlists],
};
